import {
  leaveApprovalDelegateSchema,
  leaveRequestDecideSchema,
  leaveRequestDetailSchema,
  leaveRequestEventListSchema,
  leaveInboxListSchema,
  leaveOvertimeEntrySchema,
  leaveRequestListSchema,
  leaveRequestResponseSchema,
  leaveBalanceProjectionSchema,
  leaveBalanceSchema,
  leavePersonListSchema,
  leaveAbsenceTypeListSchema,
  leaveCalendarDepartmentListSchema,
  leaveCalendarResponseSchema,
  leaveCalendarYearResponseSchema,
  leaveColleagueCalendarResponseSchema,
  leaveColleagueListSchema,
  leaveWorkingTimeStatusSchema,
  type CreateLeaveTimePunch,
  type CreateLeaveOvertimeRequest,
  type LeaveApprovalDelegateWrite,
  type LeaveRequestDecide,
  type LeaveDayPartCode,
  type LeaveRequestWrite,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const leaveBalanceKey = (year: number) => ['leave', 'balance', year] as const;
export const leaveRequestsKey = (scope: 'mine' | 'inbox' | 'all' = 'mine') =>
  ['leave', 'requests', scope] as const;
export const leaveRequestKey = (id: string) => ['leave', 'request', id] as const;
export const leaveRequestEventsKey = (id: string) => ['leave', 'request', id, 'events'] as const;
export const leaveDelegateKey = ['leave', 'delegate'] as const;
export const leaveAbsenceTypesKey = ['leave', 'absence-types'] as const;
export const leaveCalendarKey = (from: string, to: string, departmentId?: string) =>
  ['leave', 'calendar', from, to, departmentId ?? 'own'] as const;
export const leaveCalendarDepartmentsKey = ['leave', 'calendar', 'departments'] as const;
export const leaveCalendarYearKey = (year: number, userId?: string) =>
  ['leave', 'calendar', 'year', year, userId ?? 'self'] as const;
export const leaveTimeKey = (date: string) => ['leave', 'time', date] as const;

export function useLeaveBalance(year: number) {
  return useQuery({
    queryKey: leaveBalanceKey(year),
    queryFn: () => apiFetch(`/leave/me/balance?year=${year}`, leaveBalanceSchema),
  });
}

/** Rest submit would store: `balances.project` with status `submitted`, field `restAfterPending`. */
export function useLeaveBalanceProjection(input: {
  startsOn: string;
  endsOn: string;
  startPart: LeaveDayPartCode;
  endPart: LeaveDayPartCode;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [
      'leave',
      'balance',
      'projection',
      input.startsOn,
      input.endsOn,
      input.startPart,
      input.endPart,
    ],
    enabled: input.enabled,
    queryFn: () => {
      const search = new URLSearchParams({
        startsOn: input.startsOn,
        endsOn: input.endsOn,
        startPart: input.startPart,
        endPart: input.endPart,
      });
      return apiFetch(`/leave/me/balance/projection?${search}`, leaveBalanceProjectionSchema);
    },
  });
}

export function useLeaveTime(date: string) {
  return useQuery({
    queryKey: leaveTimeKey(date),
    queryFn: () =>
      apiFetch(`/leave/time?date=${encodeURIComponent(date)}`, leaveWorkingTimeStatusSchema),
  });
}

export function usePunchLeaveTime(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveTimePunch) =>
      apiFetch('/leave/time/punch', leaveWorkingTimeStatusSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(leaveTimeKey(date), data);
      await queryClient.invalidateQueries({ queryKey: leaveTimeKey(date) });
    },
  });
}

export function useConfirmLeaveTime(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(
        `/leave/time/confirm?date=${encodeURIComponent(date)}`,
        leaveWorkingTimeStatusSchema,
        {
          method: 'POST',
        },
      ),
    onSuccess: async (data) => {
      queryClient.setQueryData(leaveTimeKey(date), data);
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useLeaveRequests(scope: 'mine' | 'inbox' | 'all' = 'mine', page?: number) {
  const search = new URLSearchParams({ scope });
  if (page != null) {
    search.set('page', String(page));
    search.set('pageSize', '25');
  }
  return useQuery({
    queryKey: [...leaveRequestsKey(scope), page ?? 'all'],
    queryFn: () => apiFetch(`/leave/requests?${search.toString()}`, leaveRequestListSchema),
  });
}

export function useLeaveInbox(page?: number) {
  const search = new URLSearchParams({ scope: 'inbox' });
  if (page != null) {
    search.set('page', String(page));
    search.set('pageSize', '25');
  }
  return useQuery({
    queryKey: [...leaveRequestsKey('inbox'), page ?? 'all'],
    queryFn: () => apiFetch(`/leave/requests?${search.toString()}`, leaveInboxListSchema),
  });
}

export function useLeaveRequest(id: string) {
  return useQuery({
    queryKey: leaveRequestKey(id),
    queryFn: () => apiFetch(`/leave/requests/${id}`, leaveRequestDetailSchema),
    enabled: id.length > 0,
  });
}

export function useLeaveRequestEvents(id: string) {
  return useQuery({
    queryKey: leaveRequestEventsKey(id),
    queryFn: () => apiFetch(`/leave/requests/${id}/events`, leaveRequestEventListSchema),
    enabled: id.length > 0,
  });
}

export function useLeaveColleagues(enabled = true) {
  return useQuery({
    queryKey: ['leave', 'colleagues'] as const,
    queryFn: () => apiFetch('/leave/colleagues', leaveColleagueListSchema),
    enabled,
  });
}

export function useLeaveColleagueCalendar(from: string, to: string, enabled = true) {
  const search = new URLSearchParams({ from, to });
  return useQuery({
    queryKey: ['leave', 'colleagues', 'calendar', from, to] as const,
    queryFn: () =>
      apiFetch(
        `/leave/colleagues/calendar?${search.toString()}`,
        leaveColleagueCalendarResponseSchema,
      ),
    enabled: enabled && from.length > 0 && to.length > 0,
  });
}

export function useLeaveCalendar(from: string, to: string, departmentId?: string | null) {
  const search = new URLSearchParams({ from, to });
  if (departmentId) {
    search.set('departmentId', departmentId);
  }
  return useQuery({
    queryKey: leaveCalendarKey(from, to, departmentId ?? undefined),
    queryFn: () => apiFetch(`/leave/calendar?${search.toString()}`, leaveCalendarResponseSchema),
    enabled: from.length > 0 && to.length > 0,
  });
}

export function useLeaveCalendarDepartments(enabled: boolean) {
  return useQuery({
    queryKey: leaveCalendarDepartmentsKey,
    queryFn: () => apiFetch('/leave/calendar/departments', leaveCalendarDepartmentListSchema),
    enabled,
  });
}

export function useLeaveCalendarYear(year: number, userId?: string | null, enabled = true) {
  const search = new URLSearchParams({ year: String(year) });
  if (userId) {
    search.set('userId', userId);
  }
  return useQuery({
    queryKey: leaveCalendarYearKey(year, userId ?? undefined),
    queryFn: () =>
      apiFetch(`/leave/calendar/year?${search.toString()}`, leaveCalendarYearResponseSchema),
    enabled: enabled && Number.isInteger(year) && year >= 2000 && year <= 2100,
  });
}

export function useLeaveAbsenceTypes() {
  return useQuery({
    queryKey: leaveAbsenceTypesKey,
    queryFn: () => apiFetch('/leave/absence-types', leaveAbsenceTypeListSchema),
  });
}

export function useLeavePeople(
  q: string,
  options: { includeSelf?: boolean; minLength?: number } = {},
) {
  const minLength = options.minLength ?? 2;
  const includeSelf = options.includeSelf ?? false;
  return useQuery({
    queryKey: ['leave', 'people', q, includeSelf, minLength] as const,
    queryFn: () => {
      const params = new URLSearchParams({ q });
      if (includeSelf) {
        params.set('includeSelf', '1');
      }
      return apiFetch(`/leave/people?${params.toString()}`, leavePersonListSchema);
    },
    enabled: q.trim().length >= minLength,
  });
}

export function useLeaveDelegate() {
  return useQuery({
    queryKey: leaveDelegateKey,
    queryFn: () => apiFetch('/leave/delegates', leaveApprovalDelegateSchema),
  });
}

export function useCreateLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveRequestWrite) =>
      apiFetch('/leave/requests', leaveRequestResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave', 'requests'] });
    },
  });
}

export function useSubmitLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/leave/requests/${id}/submit`, leaveRequestResponseSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useCancelLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/leave/requests/${id}/cancel`, leaveRequestResponseSchema, { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useCreateOwnLeaveOvertime() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeaveOvertimeRequest) =>
      apiFetch('/leave/overtime', leaveOvertimeEntrySchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useDecideLeaveOvertime() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeaveRequestDecide }) =>
      apiFetch(`/leave/overtime/${id}/decide`, leaveOvertimeEntrySchema, {
        method: 'POST',
        body: JSON.stringify(leaveRequestDecideSchema.parse(body)),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useDecideLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeaveRequestDecide }) =>
      apiFetch(`/leave/requests/${id}/decide`, leaveRequestDetailSchema, {
        method: 'POST',
        body: JSON.stringify(leaveRequestDecideSchema.parse(body)),
      }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
      await queryClient.invalidateQueries({ queryKey: leaveRequestKey(variables.id) });
    },
  });
}

export function useUpsertLeaveDelegate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveApprovalDelegateWrite) =>
      apiFetch('/leave/delegates', leaveApprovalDelegateSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leaveDelegateKey });
    },
  });
}
